import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import {
  useGoogleVerifyConfig,
  validateGoogleCodeInput,
} from '../../hooks/useGoogleVerifyConfig';
import { formatOptionalId } from './memberConstants';

export default function MemberBalanceAdjustModal({ open, row, onClose, onSuccess }) {
  const { config } = useGoogleVerifyConfig();
  const [direction, setDirection] = useState('CREDIT');
  const [amount, setAmount] = useState('');
  const [remark, setRemark] = useState('');
  const [googleCode, setGoogleCode] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const showGoogleField = !config.skip;

  useEffect(() => {
    if (!open) {
      return;
    }
    setDirection('CREDIT');
    setAmount('');
    setRemark('');
    setGoogleCode('');
    setError('');
    setSubmitting(false);
  }, [open, row?.id]);

  if (!open || !row) {
    return null;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (!amount || Number(amount) <= 0) {
      setError('請填寫調整金額');
      return;
    }
    const googleError = config.skip ? '' : validateGoogleCodeInput(config, googleCode);
    if (googleError) {
      setError(googleError);
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const payload = {
        direction,
        memberId: row.id,
        amount: Number(amount),
        remark: remark?.trim() || undefined,
      };
      const code = googleCode?.trim();
      if (code) {
        payload.googleCode = code;
      }
      await adminApi.adjustBalance(payload);
      onSuccess?.();
      onClose?.();
    } catch (err) {
      setError(err.message || '操作失敗');
    } finally {
      setSubmitting(false);
    }
  }

  function handleReset() {
    setDirection('CREDIT');
    setAmount('');
    setRemark('');
    setGoogleCode('');
    setError('');
  }

  return (
    <div className="admin-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="admin-modal admin-modal--role-edit"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="admin-modal__header">
          <h3>修改餘額 [{formatOptionalId(row.id)}]</h3>
          <button type="button" className="admin-modal__close" onClick={onClose}>
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="admin-modal__body admin-modal__body--form">
            <div className="form-field">
              <label>用戶 ID</label>
              <input type="text" value={row.id} disabled />
            </div>
            <fieldset className="form-field">
              <legend>
                <span className="required-mark">*</span> 調整類型
              </legend>
              <label className="radio-label">
                <input
                  type="radio"
                  name="adjustDirection"
                  checked={direction === 'CREDIT'}
                  onChange={() => setDirection('CREDIT')}
                />
                提高金額
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="adjustDirection"
                  checked={direction === 'DEBIT'}
                  onChange={() => setDirection('DEBIT')}
                />
                減少金額
              </label>
            </fieldset>
            <div className="form-field">
              <label htmlFor="adjustAmount">
                <span className="required-mark">*</span> 調整金額
              </label>
              <input
                id="adjustAmount"
                type="number"
                min="0.01"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>
            <div className="form-field">
              <label htmlFor="adjustRemark">備註</label>
              <input
                id="adjustRemark"
                type="text"
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
              />
            </div>
            {showGoogleField && (
              <div className="form-field">
                <label htmlFor="adjustGoogleCode">
                  {!config.skip && <span className="required-mark">*</span>} 谷歌驗證碼
                </label>
                <input
                  id="adjustGoogleCode"
                  type="text"
                  inputMode="numeric"
                  value={googleCode}
                  onChange={(e) => setGoogleCode(e.target.value)}
                  placeholder={config.skip ? '跳過開啟時可不填' : '請輸入 6 位驗證碼'}
                />
              </div>
            )}

            {error && <div className="alert alert-error">{error}</div>}
          </div>
          <div className="admin-modal__footer admin-modal__footer--center">
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? '提交中…' : '提交'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleReset} disabled={submitting}>
              重置
            </button>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>
              關閉
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
